import { useCallback, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2 } from '../../icons';
import {
  updateDocType, FIELD_KINDS,
  type DocTypeDto, type FormField, type FieldKind, type FieldSource,
} from '../../api/approval-doc-type';
import { COLUMN_KINDS, type ColumnKind } from '../../lib/approval-formula';
import './ApprovalForm.css';

/**
 * 서식 만들기 — **문서 종류마다 다른 칸** (2026-08-28 · 자리 재정의 판 9).
 *
 * ★★ **칸 종류는 회사가 못 만든다** — 열이 전부다. 새 종류는 그 값을 받아 그리고 검사하는
 *    코드가 함께 있어야 뜻이 생긴다(서버 `form-schema.ts` 가 SSOT).
 *
 * ★★ **「고르기」의 목록을 여기서 만들지 않는다.** 휴가 종류는 근태 기준 설정이, 부서는
 *    조직도가 주인이다 — 여기서는 **주인을 고르기만** 한다. 목록을 서식에 베껴 두면
 *    한 값을 두 자리에서 고치게 되고, 반드시 한쪽만 고쳐진다.
 *
 * ★ **서식을 바꿔도 이미 올라간 문서는 안 바뀐다** — 그 문서는 제 스냅샷에 그때의 칸 정의를
 *   이고 있다. 화면이 그 사실을 적는다(안 적으면 「옛 문서가 깨지나」를 걱정하게 된다).
 *
 * ★★ **팝업에서 「칸」 탭으로 내려왔다** (2026-08-31 · 시안 맞춤 7/9). 서식을 고치는 일은
 *    이 양식을 손보는 일의 **한 부분**이지 딴 화면이 아니다 — 팝업이면 기본·결재선·전결을
 *    보면서 칸을 못 고친다. 표로 바꾼 것도 같은 이유다(칸 여덟 줄이 카드 여덟 장보다 짧다).
 * ★ **설명(`hint`) 칸이 없었다** — 서버 규약에는 처음부터 있었는데 고칠 자리가 없어
 *   아무도 못 썼다(칸 아래 한 줄 안내가 기안자에게 가장 큰 도움인데).
 */

const KIND_LABEL: Record<FieldKind, string> = {
  text: '한 줄', textarea: '여러 줄', number: '숫자', date: '날짜',
  period: '기간', select: '고르기', user: '사람', table: '표',
  time: '시각', formula: '계산식',
};

/** 표 열의 종류 — 합계를 세려면 그 열이 숫자여야 한다 (2026-09-01 · 판 13). */
const COLUMN_KIND_LABEL: Record<ColumnKind, string> = {
  text: '글자', number: '숫자', date: '날짜', money: '금액',
  // ★ 2026-09-04(예산 판 B0) — 계정과목처럼 **코드로 갈리는 것**을 표 안에서 고르게 한다.
  select: '고르기',
};

/** 계산식이 보이는 꼴. 값 자체는 숫자 하나다. */
const FORMAT_LABEL: Record<'number' | 'money' | 'hours', string> = {
  number: '숫자', money: '금액', hours: '시간',
};

/**
 * 「고르기」의 목록 주인 — **여기 있는 것이 전부**다(주인 없는 목록은 직접 적는다).
 *
 * ★ `owner`·`where` 는 화면 아래 「어디서 목록을 받나」 표가 쓴다 (2026-08-31 · 시안 F2).
 *   같은 배열이 **고르는 자리와 설명하는 자리**를 함께 먹여야 둘이 갈리지 않는다.
 */
const SOURCE_OPTIONS: Array<{
  value: string; label: string; make: () => FieldSource;
  /** 목록의 주인 — 표의 가운데 칸. */ owner: string;
  /** 그 값을 어디서 정하나 — 표의 오른쪽 칸. */ where: string;
}> = [
  {
    value: 'inline', label: '직접 적기', make: () => ({ kind: 'inline', options: [] }),
    owner: '이 양식', where: '여기서 직접 적습니다 — 다른 데서 안 씁니다',
  },
  {
    value: 'leave_type', label: '휴가 종류 (근태 기준 설정)',
    make: () => ({ kind: 'company_code', setKey: 'leave_type' }),
    owner: '근태 기준', where: '근태 › 기준 설정에서 정한 것을 받아 씁니다',
  },
  {
    value: 'trip_kind', label: '출장 · 외근 종류 (근태 기준 설정)',
    make: () => ({ kind: 'company_code', setKey: 'trip_kind' }),
    owner: '근태 기준', where: '근태 › 기준 설정에서 정한 것을 받아 씁니다',
  },
  {
    value: 'employment_type', label: '고용형태 (인사 기준 설정)',
    make: () => ({ kind: 'company_code', setKey: 'employment_type' }),
    owner: '인사 기준', where: '인사 › 기준 설정에서 정한 것을 받아 씁니다',
  },
  /**
   * ★ 계정과목 (2026-08-29 · 판 10) — 판 9 가 「주인이 아직 없다」고 적어 둔 자리다.
   *   지출결의서 종류에 «계정과목(고르기) + 금액(숫자·전결기준)» 두 칸을 두면 그것으로 돈다 —
   *   서버 코드를 더 쓰지 않고. 목록의 주인은 **결재 › 기준 설정**(회사 관리자가 정한다).
   */
  {
    value: 'account_code', label: '계정과목 (결재 기준 설정)',
    make: () => ({ kind: 'company_code', setKey: 'account_code' }),
    owner: '계정과목', where: '전자결재 › 계정과목에서 정합니다',
  },
  {
    value: 'division', label: '부서 (조직도)', make: () => ({ kind: 'org', level: 'division' }),
    owner: '조직도', where: '통합설정 › 조직 › 조직도에서 정합니다',
  },
  {
    value: 'team', label: '팀 (조직도)', make: () => ({ kind: 'org', level: 'team' }),
    owner: '조직도', where: '통합설정 › 조직 › 조직도에서 정합니다',
  },
];

const sourceValue = (s: FieldSource | undefined): string =>
  s == null ? 'inline'
    : s.kind === 'inline' ? 'inline'
    : s.kind === 'company_code' ? s.setKey
    : s.level;

const newField = (n: number): FormField => ({ key: `field${n}`, label: `칸 ${n}`, kind: 'text' });

interface Props {
  docType: DocTypeDto;
  /** 있으면 「닫기」가 함께 선다 — 팝업으로 쓸 때만. 탭 안에서는 닫을 것이 없다. */
  onClose?: () => void;
  onSaved: (next: DocTypeDto) => void;
}

export function FormSchemaEditor({ docType, onClose, onSaved }: Props) {
  const [fields, setFields] = useState<FormField[]>(() => docType.formSchema?.fields ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 한 칸 고치기.
   *
   * ★ `undefined` 로 준 칸은 **지운다**. 남겨 두면 「표였다가 한 줄이 된 칸」이 `columns` 를
   *   계속 이고 다닌다 — JSON 으로 나갈 때는 사라지지만, 화면 상태가 실제와 달라지면
   *   다음 판단(무엇이 남아 있나)이 어긋난다.
   */
  const patch = useCallback((i: number, next: Partial<FormField>) => {
    setFields((prev) => prev.map((f, x) => {
      if (x !== i) return f;
      const merged = { ...f, ...next } as Record<string, unknown>;
      for (const [k, v] of Object.entries(next)) if (v === undefined) delete merged[k];
      return merged as unknown as FormField;
    }));
  }, []);

  const move = useCallback((i: number, dir: -1 | 1) => {
    setFields((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  }, []);

  /** 전결 기준은 **하나만** — 새로 켜면 다른 칸의 표시는 꺼진다(서버도 둘이면 400). */
  const setMeasure = useCallback((i: number, on: boolean) => {
    setFields((prev) => prev.map((f, x) => {
      const { measure: _drop, ...rest } = f;
      return x === i && on ? { ...rest, measure: true } : rest;
    }));
  }, []);

  const dupKey = useMemo(() => {
    const seen = new Set<string>();
    for (const f of fields) {
      if (seen.has(f.key)) return f.key;
      seen.add(f.key);
    }
    return null;
  }, [fields]);

  const save = useCallback(async () => {
    if (dupKey) { setError(`이름표가 겹칩니다 — ${dupKey}`); return; }
    setBusy(true);
    setError(null);
    try {
      // ★ 칸이 하나도 없으면 `null` 을 보낸다 — «서식 없음»으로 되돌린다(종전 모습).
      const next = await updateDocType(docType.id, {
        formSchema: fields.length > 0 ? { fields } : null,
      });
      onSaved(next);
      onClose?.();
    } catch (e) {
      const problem = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(problem ?? '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }, [dupKey, fields, docType.id, onSaved, onClose]);

  /** 저장할 것이 남았나 — 안 고쳤는데 저장이 눌리면 무엇이 바뀌었는지 알 수 없다. */
  const dirty = useMemo(
    () => JSON.stringify(fields) !== JSON.stringify(docType.formSchema?.fields ?? []),
    [fields, docType.formSchema],
  );

  /**
   * ★★★ **근태와 연결된 양식은 서식을 못 고친다** (2026-09-01 · 사용자 지시).
   *
   *    *「휴가·연차 등 근태와 관련된 전자결재는 시안이 변경되면 안 된다.」*
   *
   *    이 칸들은 결재 안에서만 사는 값이 아니라 **근태가 읽는 재료**다. 고칠 수 있는 것처럼
   *    보여 놓고 서버가 막으면 그것도 거짓말이라, 화면이 먼저 말한다(서버도 따로 막는다).
   * ★ 잠긴 것은 서식뿐 — 이름·결재 단계·전결 기준은 다른 탭에서 그대로 고친다.
   */
  if (docType.schemaLocked) {
    return (
      <div className="fse">
        <p className="fse__locked" role="note">
          <b>이 양식의 칸은 바꿀 수 없습니다.</b> 여기 적힌 값이 그대로 <b>근태 기록의 재료</b>가
          되기 때문입니다 — 「기간」이 시작·끝 두 날짜여야 휴가로 옮길 수 있습니다.
          <br />
          이름 · 결재 단계 · 전결 기준은 다른 탭에서 고칠 수 있습니다.
        </p>
        <ul className="fse__lockedlist">
          {(docType.formSchema?.fields ?? []).map((f) => (
            <li key={f.key} className="fse__lockeditem">
              <b>{f.label}</b>
              <span className="fse__lockedkind">{f.kind}{f.required ? ' · 필수' : ''}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="fse">
      {error && <p className="apf__warn" role="alert">{error}</p>}

      {fields.length === 0 ? (
        <p className="apb__note">아직 칸이 없습니다 — 이 종류는 제목과 본문만 받습니다.</p>
      ) : (
        <div className="fse__wrap">
          <table className="fse__table">
            <thead>
              <tr>
                <th scope="col" className="fse__c-no">순서</th>
                <th scope="col">이름</th>
                <th scope="col" className="fse__c-kind">칸 종류</th>
                <th scope="col" className="fse__c-req">필수</th>
                <th scope="col">설명 · 목록</th>
                <th scope="col" className="fse__c-msr">전결 기준</th>
                <th scope="col" className="fse__c-act"><span className="fse__sr">순서 · 지우기</span></th>
              </tr>
            </thead>
            <tbody>
              {fields.map((f, i) => (
                <tr key={i}>
                  <td className="fse__no">{i + 1}</td>
                  <td>
                    <input
                      className="apf__input fse__label" value={f.label}
                      aria-label={`${i + 1}번째 칸 이름`}
                      onChange={(e) => patch(i, { label: e.target.value })}
                    />
                    {/* 이름표(`key`) 는 값이 저장되는 열쇠다 — 기안자에게는 안 보이지만
                        이미 올라간 문서가 이 글자로 값을 찾으므로 화면에 세워 둔다. */}
                    <input
                      className="apf__input fse__key" value={f.key}
                      aria-label={`${i + 1}번째 칸 이름표`}
                      onChange={(e) => patch(i, { key: e.target.value })}
                    />
                  </td>
                  <td>
                    <select
                      className="apf__input fse__kind" value={f.kind}
                      aria-label={`${i + 1}번째 칸 종류`}
                      onChange={(e) => {
                        const kind = e.target.value as FieldKind;
                        patch(i, {
                          kind,
                          // 종류를 바꾸면 그 종류에만 있던 설정을 버린다 — 남으면 서버가 400 을 낸다.
                          source: kind === 'select' ? (f.source ?? { kind: 'inline', options: [] }) : undefined,
                          columns: kind === 'table' ? (f.columns ?? ['항목']) : undefined,
                          columnKinds: kind === 'table' ? f.columnKinds : undefined,
                          expr: kind === 'formula' ? (f.expr ?? '') : undefined,
                          format: kind === 'formula' ? f.format : undefined,
                          decimals: kind === 'formula' ? f.decimals : undefined,
                          // ★ 전결 기준은 **숫자를 내는 칸**에만 남긴다.
                          measure: kind === 'number' || kind === 'formula' ? f.measure : undefined,
                        });
                      }}
                    >
                      {FIELD_KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                    </select>
                  </td>
                  <td className="fse__mid">
                    <input
                      type="checkbox" checked={f.required === true}
                      aria-label={`${f.label} 필수`}
                      onChange={(e) => patch(i, { required: e.target.checked || undefined })}
                    />
                  </td>
                  <td>
                    {/* ★ 설명은 **칸 아래 한 줄**로 기안자에게 간다 — 여기 말고는 알려 줄 자리가 없다. */}
                    <input
                      className="apf__input" value={f.hint ?? ''}
                      placeholder="칸 아래에 뜰 한 줄 안내"
                      aria-label={`${f.label} 설명`}
                      onChange={(e) => patch(i, { hint: e.target.value || undefined })}
                    />

                    {f.kind === 'select' && (
                      <>
                        <select
                          className="apf__input fse__src" value={sourceValue(f.source)}
                          aria-label={`${f.label} 목록`}
                          onChange={(e) => {
                            const opt = SOURCE_OPTIONS.find((o) => o.value === e.target.value);
                            patch(i, { source: opt ? opt.make() : { kind: 'inline', options: [] } });
                          }}
                        >
                          {SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        {f.source?.kind === 'inline' ? (
                          <input
                            className="apf__input" placeholder="쉼표로 나눠 적습니다 — 예: 예, 아니오"
                            aria-label={`${f.label} 목록 값`}
                            value={f.source.options.join(', ')}
                            onChange={(e) => patch(i, {
                              source: {
                                kind: 'inline',
                                options: e.target.value.split(',').map((v) => v.trim()).filter(Boolean),
                              },
                            })}
                          />
                        ) : (
                          /* ★ 주인이 있는 목록은 **여기서 안 적는다** — 그 사실을 화면이 말한다. */
                          <span className="fse__owned">주인의 화면에서 정한 것을 받아 씁니다</span>
                        )}
                      </>
                    )}

                    {f.kind === 'table' && (
                      <>
                        <input
                          className="apf__input" placeholder="열 이름을 쉼표로 — 예: 항목, 금액"
                          aria-label={`${f.label} 열`}
                          value={(f.columns ?? []).join(', ')}
                          onChange={(e) => {
                            const cols = e.target.value.split(',').map((v) => v.trim()).filter(Boolean);
                            // ★ 열이 줄거나 늘면 종류도 같이 맞춘다 — 길이가 다르면 서버가 400 을 낸다.
                            const prev = f.columnKinds ?? [];
                            const kinds = cols.map((_, k) => prev[k] ?? 'text');
                            // ★ 목록도 같이 맞춘다 — 길이가 다르면 서버가 400 을 낸다(예산 판 B0).
                            const srcs = cols.map((_, k) =>
                              (kinds[k] === 'select'
                                ? ((f.columnSources ?? [])[k] ?? { kind: 'inline', options: [] } as FieldSource)
                                : null));
                            patch(i, {
                              columns: cols,
                              columnKinds: kinds.some((k) => k !== 'text') ? kinds : undefined,
                              columnSources: kinds.includes('select') ? srcs : undefined,
                            });
                          }}
                        />
                        {/*
                          ★★ **열마다 종류를 준다** (판 13) — 합계를 세려면 그 열이 숫자여야 한다.
                             종류를 안 정하면 전부 글자 열이고, 그것이 **옛 서식의 모습**이다.
                        */}
                        {(f.columns ?? []).length > 0 && (
                          <table className="fse__cols">
                            <tbody>
                              {(f.columns ?? []).map((c, ci) => (
                                <tr key={c}>
                                  <td>{c}</td>
                                  <td>
                                    <select
                                      className="apf__input" aria-label={`${f.label} ${c} 열 종류`}
                                      value={(f.columnKinds ?? [])[ci] ?? 'text'}
                                      onChange={(e) => {
                                        const next = e.target.value as ColumnKind;
                                        const kinds = (f.columns ?? []).map((_, k) =>
                                          k === ci ? next : ((f.columnKinds ?? [])[k] ?? 'text'));
                                        /**
                                         * ★★ **고르기 열은 목록 주인이 딸려야 한다** (예산 판 B0) —
                                         *    없으면 서버가 400 을 낸다. 종류를 바꾸는 순간 기본 주인을
                                         *    함께 세워 «고를 수 없는 고르기 열» 이 생기지 않게 한다.
                                         */
                                        const srcs = (f.columns ?? []).map((_, k) =>
                                          (kinds[k] === 'select'
                                            ? ((f.columnSources ?? [])[k] ?? { kind: 'inline', options: [] } as FieldSource)
                                            : null));
                                        patch(i, {
                                          columnKinds: kinds.some((k) => k !== 'text') ? kinds : undefined,
                                          columnSources: kinds.includes('select') ? srcs : undefined,
                                        });
                                      }}
                                    >
                                      {COLUMN_KINDS.map((k) => <option key={k} value={k}>{COLUMN_KIND_LABEL[k]}</option>)}
                                    </select>
                                  </td>
                                  <td>
                                    {/* ★ 고르기 열만 목록을 묻는다 — 나머지 열은 물을 것이 없다. */}
                                    {(f.columnKinds ?? [])[ci] === 'select' && (
                                      <select
                                        className="apf__input" aria-label={`${f.label} ${c} 열 목록`}
                                        value={sourceValue((f.columnSources ?? [])[ci] ?? undefined)}
                                        onChange={(e) => {
                                          const opt = SOURCE_OPTIONS.find((o) => o.value === e.target.value);
                                          const srcs = (f.columns ?? []).map((_, k) =>
                                            (k === ci
                                              ? (opt ? opt.make() : { kind: 'inline', options: [] } as FieldSource)
                                              : ((f.columnSources ?? [])[k] ?? null)));
                                          patch(i, { columnSources: srcs });
                                        }}
                                      >
                                        {SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                      </select>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </>
                    )}

                    {/*
                      ★★★ **계산식** (판 13) — 칸을 «이름» 이 아니라 **열쇠**로 가리킨다.
                         이름을 바꿔도 식이 안 깨지게 하려는 것이다. 아래 표가 무엇을 쓸 수 있는지
                         그대로 보여 준다 — 안 보여 주면 열쇠를 어디서 알아내나.
                    */}
                    {f.kind === 'formula' && (
                      <>
                        <input
                          className="apf__input fse__expr" placeholder="예: 합계([items.금액])"
                          aria-label={`${f.label} 계산식`}
                          value={f.expr ?? ''}
                          onChange={(e) => patch(i, { expr: e.target.value })}
                        />
                        <span className="fse__owned">
                          쓸 수 있는 것 — {fields.filter((x) => x.key !== f.key).map((x) => (
                            x.kind === 'table'
                              ? (x.columns ?? []).map((c) => `[${x.key}.${c}]`).join(' ')
                              : `[${x.key}]`
                          )).join(' ') || '아직 다른 칸이 없습니다'}
                          {' · '}함수 합계 개수 시간차 일수 반올림
                        </span>
                        <span className="fse__fmt">
                          <select
                            className="apf__input" aria-label={`${f.label} 표시`}
                            value={f.format ?? 'number'}
                            onChange={(e) => patch(i, { format: e.target.value as 'number' | 'money' | 'hours' })}
                          >
                            {(['number', 'money', 'hours'] as const).map((k) => (
                              <option key={k} value={k}>{FORMAT_LABEL[k]}</option>
                            ))}
                          </select>
                          <input
                            className="apf__input" type="number" min={0} max={6}
                            aria-label={`${f.label} 소수 자릿수`}
                            value={f.decimals ?? 0}
                            onChange={(e) => patch(i, { decimals: Number(e.target.value) })}
                          />
                        </span>
                      </>
                    )}
                  </td>
                  <td className="fse__mid">
                    {/*
                      ★ 전결 기준은 **숫자를 내는 칸**에만 — 글자 칸을 기준으로 삼으면 전결이 못 돈다.
                      ★★ 계산식도 붙을 수 있다(판 13) — 지출 「합계」가 바로 그 자리다.
                    */}
                    {f.kind === 'number' || f.kind === 'formula' ? (
                      <input
                        type="checkbox" checked={f.measure === true}
                        aria-label={`${f.label} 전결 기준`}
                        onChange={(e) => setMeasure(i, e.target.checked)}
                      />
                    ) : <span className="fse__dash">—</span>}
                  </td>
                  <td className="fse__acts">
                    <button type="button" className="apf__rowdel" aria-label={`${i + 1}번째 칸 위로`} onClick={() => move(i, -1)}>
                      <ChevronUp size={15} aria-hidden />
                    </button>
                    <button type="button" className="apf__rowdel" aria-label={`${i + 1}번째 칸 아래로`} onClick={() => move(i, 1)}>
                      <ChevronDown size={15} aria-hidden />
                    </button>
                    <button
                      type="button" className="apf__rowdel" aria-label={`${i + 1}번째 칸 지우기`}
                      onClick={() => setFields((prev) => prev.filter((_, x) => x !== i))}
                    >
                      <Trash2 size={15} aria-hidden />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="fse__foot">
        <button
          type="button" className="apf__rowadd"
          onClick={() => setFields((prev) => [...prev, newField(prev.length + 1)])}
        >
          <Plus size={14} aria-hidden /> 칸 추가
        </button>
        <span className="fse__sp" />
        {onClose && (
          <button type="button" className="apf__rowadd" onClick={onClose}>닫기</button>
        )}
        <button
          type="button" className="fse__save"
          onClick={() => { void save(); }}
          disabled={busy || !dirty}
        >
          {busy ? '저장 중…' : '저장'}
        </button>
      </div>

      {/*
        ★★ **「고르기」의 목록은 여기서 만들지 않는다** (2026-08-31 · 시안 F2 의 카드 그대로).
           종전에는 이 사실이 아래 문단 한 줄에 묻혀 있어, 목록을 어디서 고치는지 알려면
           칸을 하나 만들어 «출처» 선택지를 열어 봐야 했다. 표로 세우면 **열기 전에** 보인다.
        ★ 같은 `SOURCE_OPTIONS` 로 그린다 — 고르는 자리와 설명하는 자리가 갈리지 않는다.
      */}
      <section className="fse__srcbox" aria-label="고르기 칸의 목록 주인">
        <h3 className="fse__srct">
          「고르기」 칸은 어디서 목록을 받나
          <span className="fse__srcbd">기준 설정과 이어진다</span>
        </h3>
        <table className="fse__srctbl">
          <tbody>
            {SOURCE_OPTIONS.map((o) => (
              <tr key={o.value}>
                <th scope="row">{o.label.replace(/\s*\(.*\)$/, '')}</th>
                <td><span className="fse__srcown">{o.owner}</span></td>
                <td className="fse__srcwhere">{o.where}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <p className="apb__note">
        칸 종류는 열입니다 — 한 줄 · 여러 줄 · 숫자 · 날짜 · 기간 · 고르기 · 사람 · 표 · 시각 · 계산식.
        <b> 전결 기준은 한 칸만</b> 고를 수 있습니다(둘을 고르면 어느 값으로 잘랐는지 나중에 설명할 수 없습니다).
        칸이 없으면 지금처럼 <b>제목과 본문만</b> 받습니다.
      </p>
      <p className="apb__note">
        서식을 바꿔도 <strong>이미 올라간 문서는 바뀌지 않습니다</strong> — 그 문서는 상신할 때의
        칸 정의를 스스로 이고 있습니다. 「고르기」의 목록은 <strong>주인이 따로</strong> 있어
        (근태 · 인사 기준 설정 · 계정과목 · 조직도) 여기서 고치지 않습니다.
      </p>
    </div>
  );
}
